/**
* ─────────────────────────────────────────────────────────────────────
* FILE: src/pages/SignUp.tsx
* DESCRIPTION: Custom sign-up page with VeriMark branding + Waitlist Fallback
* @copyright 2025-2026 Anou. All rights reserved.
*
* UPDATES:
* - 2026-06-09: Added explicit waitlist consent flow
*               - Intercepts "sign-ups unavailable" Clerk error
*               - Shows inline prompt with pre-filled email
*               - Securely calls backend waitlist API
*               - Preserves existing verification & social sign-up logic
* ─────────────────────────────────────────────────────────────────────
*/
import { useState } from 'react'
import { useSignUp } from '@clerk/react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, CheckCircle } from 'lucide-react'

export default function SignUp() {
  const { signUp } = useSignUp()
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [verificationCode, setVerificationCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [needsVerification, setNeedsVerification] = useState(false)
  
  // Waitlist flow state
  const [showWaitlistPrompt, setShowWaitlistPrompt] = useState(false)
  const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false)
  const [waitlistSuccess, setWaitlistSuccess] = useState(false)
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setShowWaitlistPrompt(false)
    setWaitlistSuccess(false)

    if (!signUp) {
      setError('Sign-up is not ready yet')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    const handleClerkError = (err: any) => {
      const errorObj = err?.errors?.[0] ?? err?.error ?? err

      const code = errorObj?.code ?? ''

      const message =
        errorObj?.message ??
        errorObj?.long_message ??
        errorObj?.longMessage ??
        err?.message ??
        'Failed to create account'

      const longMessage =
        errorObj?.long_message ??
        errorObj?.longMessage ??
        ''

      const isWaitlistError =
        code === 'sign_up_restricted_waitlist' ||
        /waitlist/i.test(message) ||
        /waitlist/i.test(longMessage)

      if (isWaitlistError) {
        setError('')
        setShowWaitlistPrompt(true)
        return true
      }

      setError(message)
      return true
    }

    setIsLoading(true)

    try {
      const result: any = await signUp.create({
        emailAddress: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      })

      // IMPORTANT: Clerk may return errors instead of throwing.
      if (result?.errors?.length) {
        handleClerkError(result)
        return
      }

      if (result?.error) {
        handleClerkError(result)
        return
      }

      if (result?.status === 'complete' || signUp.status === 'complete') {
        await signUp.finalize({
          navigate: () => navigate('/dashboard'),
        })
        return
      }

      const verificationResult: any = await signUp.verifications.sendEmailCode()

      if (verificationResult?.errors?.length) {
        handleClerkError(verificationResult)
        return
      }

      if (verificationResult?.error) {
        handleClerkError(verificationResult)
        return
      }

      setNeedsVerification(true)
    } catch (err: any) {
      handleClerkError(err)
    } finally {
      setIsLoading(false)
    }
  }
  
const handleVerifyEmail = async (e: React.FormEvent) => {
  e.preventDefault()
  setError('')
  setIsLoading(true)
  try {
    if (!signUp) {
      setError('Sign-up is not ready yet')
      return
    }
    const { error } = await signUp.verifications.verifyEmailCode({
      code: verificationCode,
    })
    if (error) {
      setError(error.message || 'Invalid verification code')
      return
    }
    if (signUp.status === 'complete') {
      await signUp.finalize({
        navigate: () => navigate('/profile'), 
      })
    }
  } catch (err: any) {
    setError(err.errors?.[0]?.message || 'Email verification failed')
  } finally {
    setIsLoading(false)
  }
}

  const handleSocialSignUp = async (
    strategy: 'oauth_google' | 'oauth_github'
  ) => {
    setError('')
    setShowWaitlistPrompt(false)
    setWaitlistSuccess(false)

    if (!signUp) {
      setError('Sign-up is not ready yet')
      return
    }

    const handleClerkError = (err: any) => {
      const errorObj = err?.errors?.[0] ?? err?.error ?? err

      const code = errorObj?.code ?? ''

      const message =
        errorObj?.message ??
        errorObj?.long_message ??
        errorObj?.longMessage ??
        err?.message ??
        'Social sign-up failed'

      const longMessage =
        errorObj?.long_message ??
        errorObj?.longMessage ??
        ''

      const isWaitlistError =
        code === 'sign_up_restricted_waitlist' ||
        /waitlist/i.test(message) ||
        /waitlist/i.test(longMessage)

      if (isWaitlistError) {
        setError('')
        setShowWaitlistPrompt(true)
        return true
      }

      setError(message)
      return true
    }

    try {
      const result: any = await signUp.sso({
        strategy,
        redirectCallbackUrl: '/sso-callback',
        redirectUrl: '/dashboard',
      })

      if (result?.errors?.length) {
        handleClerkError(result)
        return
      }

      if (result?.error) {
        handleClerkError(result)
        return
      }
    } catch (err: any) {
      handleClerkError(err)
    }
  }

  const handleJoinWaitlist = async () => {
    if (!formData.email) {
      setError('Email is required to join the waitlist')
      return
    }
    setIsJoiningWaitlist(true)
    setError('')
    try {
      const res = await fetch('/api/join-waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to join waitlist')
      setWaitlistSuccess(true)
      setShowWaitlistPrompt(false)
    } catch (err: any) {
      setError(err.message || 'Failed to join waitlist')
    } finally {
      setIsJoiningWaitlist(false)
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Email Verification View
  // ─────────────────────────────────────────────────────────────
  if (needsVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg mb-6">
            <CheckCircle className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Check your email</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            We sent a verification code to <strong>{formData.email}</strong>
          </p>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm" role="alert">
                {error}
              </div>
            )}
            <form onSubmit={handleVerifyEmail} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center tracking-widest"
                placeholder="Enter code"
              />
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Verifying...' : 'Verify email'}
              </button>
            </form>
            <button
              type="button"
              onClick={() => setNeedsVerification(false)}
              className="mt-4 text-sm text-blue-600 hover:text-blue-500 font-medium"
            >
              Use another email
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────
  // Main Sign-Up View
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Logo & Header  */}
        <div className="text-center mb-8">
          <img 
            src="/logo.svg"
            alt="Logo"
            className="mx-auto h-16 w-auto object-contain mb-4 drop-shadow-sm"
          />
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">Create your account</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Join and start verifying media</p>

        {/* Sign In Link */}
        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <Link to="/sign-in" className="font-medium text-blue-600 hover:text-blue-500">Sign in</Link>
        </p>
        
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm" role="alert">
                {error}
              </div>
            )}

            {/* Waitlist Prompt UI */}
            {showWaitlistPrompt && !waitlistSuccess && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">Access is currently invite-only</h3>
                <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                  Would you like to join the waitlist with <strong>{formData.email || 'this email'}</strong>? We'll notify you as soon as spots open.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleJoinWaitlist}
                    disabled={isJoiningWaitlist}
                    className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 transition-colors"
                  >
                    {isJoiningWaitlist ? 'Joining...' : 'Join Waitlist'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowWaitlistPrompt(false); setError('') }}
                    className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Waitlist Success UI */}
            {waitlistSuccess && (
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg text-center">
                <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">You're on the list! 🎉</p>
                <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">We'll email you as soon as your invite is ready.</p>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><User className="h-5 w-5 text-gray-400" /></div>
                    <input id="firstName" type="text" required value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className="appearance-none relative block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="John" />
                  </div>
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last name</label>
                  <input id="lastName" type="text" required value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className="appearance-none relative block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="Doe" />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Mail className="h-5 w-5 text-gray-400" /></div>
                  <input id="email" type="email" autoComplete="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="appearance-none relative block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="you@example.com" />
                </div>
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Lock className="h-5 w-5 text-gray-400" /></div>
                  <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="appearance-none relative block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="••••••••" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm password</label>
                <input id="confirmPassword" type={showPassword ? 'text' : 'password'} required value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} className="appearance-none relative block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="••••••••" />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || showWaitlistPrompt}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 items-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>Create account <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300 dark:border-gray-600" /></div>
            <div className="relative flex justify-center text-sm"><span className="px-2 bg-white dark:bg-gray-800 text-gray-500">Or continue with</span></div>
          </div>

          {/* Social Sign-Up */}
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => handleSocialSignUp('oauth_google')} className="w-full inline-flex justify-center py-2.5 px-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">Google</button>
            <button type="button" onClick={() => handleSocialSignUp('oauth_github')} className="w-full inline-flex justify-center py-2.5 px-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">GitHub</button>
          </div>
        </div>

        {/* Sign In Link */}
        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <Link to="/sign-in" className="font-medium text-blue-600 hover:text-blue-500">Sign in</Link>
        </p>
      </div>
    </div>
  )
}